import React, { useEffect, useState } from "react";
import InterviewService from "../../Services/interview.service";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useLoader } from "../../Context/LoaderContext/LoaderContext";
import { useAIAssessmentContext } from "../../Context/AIAssessmentContext/AIAssessmentContext";
import {
  areQuestionsGenerated,
  isSessionCompleted,
  isSessionLive,
} from "./utils/interview-utils";
import usePollingFunctions from "./hooks/usePollingFunctions";
import {
  LOADING_MESSAGES,
  STORAGE_KEYS,
} from "./constants/interview-constants";
import { InterviewSession, Question } from "../../Interface/Index";
import { getPageMetaData } from "../../Utils/meta-data";
import { useAppContext } from "../../Context/AppContext/AppContext";
import PageMetadata from "../../Components/PageMetaData/PageMetaData";
import AiAssessment from "./components/aiAssessment/AiAssessment";
import { AIAssessmentQuestion } from "../../Entities/ai-assessment.entity";
import aiInterviewService from "../../Services/ai-interview.service";
import { openNotification } from "../../Components/Notification/Index";
import useAiAssessmentEvaluate from "./hooks/useAiAssessmentEvaluate";
import useAiAssessmentViolationCapture from "./hooks/useAiAssessmentViolationCapture";
import AiAssessmentDifferentDevice from "./components/aiAssessment/AiAssessmentDifferentDevice";
import "./index.scss";
import { useCurrentApplication } from "../../Context/CurrentApplicationContext/CurrentApplicationContext";
import ApplicationJourneySteps from "../Requirements/components/ApplicationJourneySteps";

const InterviewRounds: React.FC = () => {
  const [isSameDevice, setIsSameDevice] = useState(true);
  const [terminatedStats, setTerminatedStats] = useState({
    isLimitReached: false,
    violationType: "",
  });

  const interviewService = new InterviewService();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { slug } = useParams();

  const sessionId = searchParams.get("sessionId");

  const { checkForNextRoundQuestions } = usePollingFunctions();

  const { organization } = useAppContext();

  const { jobApplication, isInitDone, rounds } = useCurrentApplication();

  const { setLoading, setLoadingMessage, clearLoader } = useLoader();

  const {
    setCurrentSession,
    setAiAssessmentDetails,
    aiAssessmentDetails,
    setIsRoundFinished,
    setAiAssessmentQuestions,
    setCurrentStep,
    clearAIAssessmentState,
    clearProctoringState,
    clearUIState,
    clearModalState,
    setShowDiffrentDevice,
    showDiffrentDevice,
    setAssessmentInitialized,
    assessmentInitialized,
  } = useAIAssessmentContext();

  const { handleEvaluatedSession } = useAiAssessmentEvaluate();

  const { capturePageRefesh } = useAiAssessmentViolationCapture();

  const handleClearAllStates = () => {
    clearAIAssessmentState();
    clearProctoringState();
    clearUIState();
    clearModalState();
  };

  // Handler function for pending session management
  const handlePendingSession = () => {
    setIsRoundFinished(false);
    setCurrentStep(-1);
    clearLoader();
  };

  // Handler function for completed session management
  const handleCompletedSession = async (session: InterviewSession) => {
    try {
      setLoadingMessage(LOADING_MESSAGES.LOADING_QUESTIONS);
      const questions = await interviewService.getQuestionsBySessionId(
        session._id,
      );

      if (!questions || questions.length === 0) {
        console.error("No questions found for the session");
        throw new Error(`For session ${session._id}, no questions found`);
      }

      console.log("Questions found for the session:", questions.length);

      const formattedData: AIAssessmentQuestion[] = questions.map(
        (question: Question) => {
          return {
            sessionId: session._id,
            questionId: question._id,
            question: question.description,
            options:
              question.options?.map((option: any) => option.description) ?? [],
            answer: question.responses?.[0]?.description ?? null,
            answerType: question.type,
            fileUrl:
              question.type === "fileUpload"
                ? (question.responses?.[0]?.fileUrl ?? null)
                : null,
          };
        },
      );

      setAiAssessmentQuestions(formattedData);
      setIsRoundFinished(true);
      setCurrentStep(formattedData.length);
    } catch (error: any) {
      console.error("Error handling completed session:", error);
      throw error;
    } finally {
      clearLoader();
    }
  };

  // Handler function for live session management in a different device
  const handleLiveSessionInDifferentDevice = (session: InterviewSession) => {
    console.log(
      "handleLiveSessionInDifferentDevice: Handling live session in a different device",
    );
    setShowDiffrentDevice(true);
    setCurrentSession(session);
    clearLoader();
    return session;
  };

  // Handler function for live session management in the same device
  const handleLiveSessionInSameDevice = async (session: InterviewSession) => {
    console.log(
      "handleLiveSessionInSameDevice: Handling live session in the same device",
    );
    await capturePageRefesh(session);
    return session;
  };

  // Handler function to handle session questions not generated
  const handleSessionQuestionsNotGenerated = async (
    session: InterviewSession,
  ): Promise<InterviewSession> => {
    console.log(
      "handleSessionQuestionsNotGenerated: Handling session questions not generated",
    );

    setLoadingMessage(LOADING_MESSAGES.GENERATING_QUESTIONS);

    const currentTime = new Date().getTime();
    const sessionUpdatedTime = new Date(session.updatedAt).getTime();
    const timeDifference = currentTime - sessionUpdatedTime;
    try {
      // If time difference is greater than 5 minutes, then generate questions else poll for the questions
      if (timeDifference >= 5 * 60 * 1000) {
        console.log(
          "handleSessionQuestionsNotGenerated: Time difference is greater than 5 minutes, generating questions",
        );
        const updatedSession = await handleSessionStateRecovery(
          session._id,
          false,
        );
        return updatedSession || session;
      } else {
        const updatedSession = await checkForNextRoundQuestions(session._id);
        return updatedSession;
      }
    } catch (error: any) {
      console.error("Error handling session questions not generated:", error);
    }
    return session;
  };

  // Handler function to handle unknown session state
  const handleSessionStateRecovery = async (
    sessionId: string,
    isEvaluationStarted: boolean,
  ) => {
    console.log("handleUnknownSessionState: Handling unknown session state");
    setLoadingMessage(LOADING_MESSAGES.RECOVERING_SESSION);
    try {
      const session = await aiInterviewService.recoverSessionState(
        sessionId,
        isEvaluationStarted,
      );
      if (!session) {
        clearLoader();
        console.error("Session Recovery failed");
        openNotification(
          "error",
          "Session Recovery failed",
          "Please reach out to support team",
        );
        return;
      }

      const isLive = isSessionLive(session);
      const isCompleted = isSessionCompleted(session);

      setCurrentSession(session);

      if (!isLive && isCompleted && isEvaluationStarted && jobApplication) {
        return await handleEvaluatedSession(session);
      }

      if (!isLive && !isCompleted && !isEvaluationStarted) {
        return handlePendingSession();
      }

      return session;
    } catch (error: any) {
      console.error("Error handling session state recovery:", error);
      clearLoader();
    }
  };

  const initializeAIAssessment = async (id: string) => {
    // Clear all states
    handleClearAllStates();

    setLoading(true);
    setLoadingMessage("Initializing AI assessment...");

    try {
      // Find session by id
      let session = await interviewService.findSessionById(id);
      if (!session) {
        console.error("Session not found");
        throw new Error(`Session with id ${id} not found`);
      }
      console.log("Session found:", session);

      // validating if questions are generated or not
      const isQuestionsGenerated = areQuestionsGenerated(session);
      if (!isQuestionsGenerated) {
        console.log(
          "InitializingAIAssessment: Questions are not generated, starting polling",
        );
        const updatedSession =
          await handleSessionQuestionsNotGenerated(session);
        session = updatedSession;
      }

      // Updating current session and ai assessment details
      const currentRound = rounds.find(
        (round) => round.round._id === session?.interviewRoundId,
      );
      console.log("Current Round found for the session:", currentRound);
      setAiAssessmentDetails({
        sessionId: session?._id || "",
        roundName: currentRound?.round.title || "",
        noOfQuestions: session?.noOfQuestionsGenerated || 0,
        answerType: currentRound?.round.answerType.toLowerCase(),
      });

      // capturing page refresh state from local storage
      const isRefreshed = localStorage.getItem(STORAGE_KEYS.REFRESH) === "true";
      const refreshedSessionId = localStorage.getItem(
        STORAGE_KEYS.REFRESHED_SESSION_ID,
      );

      const sessionToken = localStorage.getItem("sessionToken");
      // Session current state determination values
      const isLive = isSessionLive(session);
      const isCompleted = isSessionCompleted(session);
      const isSameDevice: boolean = !!(
        sessionToken &&
        session?.sessionToken &&
        session.sessionToken === sessionToken
      );
      const isPageRefreshedDuringEvaluation =
        isRefreshed && refreshedSessionId && session._id === refreshedSessionId;

      // Updating is same device and terminated stats
      setIsSameDevice(isSameDevice);
      setCurrentSession(session);

      // Case 1: Session is live and not completed and not same device
      if (isLive && !isCompleted && !isCompleted && !isSameDevice) {
        console.log(
          "initializeAIAssessment: Session is live and not completed and not same device, handling live session in a different device",
        );
        return handleLiveSessionInDifferentDevice(session);
      }

      // Case 2: Session is live and not completed and same device and page is not refreshed during evaluation
      if (
        isLive &&
        !isCompleted &&
        isSameDevice &&
        !isPageRefreshedDuringEvaluation
      ) {
        console.log(
          "initializeAIAssessment: Session is live and not completed and same device and page is not refreshed during evaluation, handling live session in the same device",
        );
        return await handleLiveSessionInSameDevice(session);
      }

      // Case 3: Session is live and not completed and same device and page is refreshed during evaluation
      if (
        isLive &&
        !isCompleted &&
        isSameDevice &&
        isPageRefreshedDuringEvaluation
      ) {
        console.log(
          "initializeAIAssessment: Session is live and not completed and same device and page is refreshed during evaluation, handling live session in the same device",
        );
        return await handleSessionStateRecovery(id, true);
      }

      // Case 4: Session is not live and completed
      if (!isLive && isCompleted) {
        console.log(
          "initializeAIAssessment: Session is not live and completed, handling completed session",
        );
        return await handleCompletedSession(session);
      }

      // Case 5: Session is not live and not completed
      if (!isLive && !isCompleted) {
        console.log(
          "initializeAIAssessment: Session is not live and not completed, handling pending session",
        );
        return handlePendingSession();
      }

      // Case 6: Unknown session state
      console.log(
        "initializeAIAssessment: Unknown session state, handling unknown session state",
      );
      return await handleSessionStateRecovery(id, false);
    } catch (error: any) {
      console.error("Error initializing AI assessment:", error);
      throw error;
    } finally {
      setAssessmentInitialized(true);
    }
  };

  // Effect for initial validation
  useEffect(() => {
    if (isInitDone) {
      // Helper to navigate to assessments page
      const navigateToAssessments = () => {
        if (slug) {
          navigate(`/${slug}/requirements?view=assessment`, { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      };

      if (!sessionId) {
        console.warn("InterviewRounds: sessionId is missing from URL");
        navigateToAssessments();
        return;
      }

      if (!jobApplication) {
        console.warn("InterviewRounds: No application found");
        openNotification(
          "warning",
          "Application Required",
          "Please apply for this job first.",
        );
        if (slug) {
          navigate(`/${slug}/requirements`, { replace: true });
        } else {
          navigate("/", { replace: true });
        }
        return;
      }

      // Valid state - initialize assessment
      initializeAIAssessment(sessionId);
    }
  }, [sessionId, jobApplication, isInitDone, navigate, slug]);

  const meta = organization
    ? getPageMetaData("airound", {
        organization,
        roundName: aiAssessmentDetails?.roundName || "AI Assessment Round",
      })
    : getPageMetaData("airound");

  if (!assessmentInitialized) {
    return null;
  }
  return (
    <>
      <PageMetadata description={meta.description} title={meta.title} />

      <div className="interview-rounds-page">
        <ApplicationJourneySteps />
        {showDiffrentDevice ? (
          <AiAssessmentDifferentDevice />
        ) : (
          <AiAssessment
            isSameDevice={isSameDevice}
            terminatedStats={terminatedStats}
          />
        )}
      </div>
    </>
  );
};

export default InterviewRounds;
